import React, { forwardRef } from 'react';

import type { MenuProps, SelectProps } from '@mui/material';
import { Select, Typography } from '@mui/material';
import clsx from 'clsx';
import { ArrowDownIcon } from 'src/components/icons/ArrowDownIcon';
import { ToolbarInput } from 'src/components/index';
import { makeStyles } from 'tss-react/mui';

export const useToolbarSelectStyles = makeStyles<
  Pick<ToolbarSelectProps, 'border' | 'disableBorderColor' | 'disabled' | 'open' | 'selectStyles'>
>()((theme, { border, disableBorderColor, disabled, open, selectStyles }) => {
  const borderProps = border
    ? {
        border: disableBorderColor
          ? `1px solid ${theme.appColors.rule}`
          : open
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.appColors.rule}`,
      }
    : {};

  return {
    select: {
      /** Turn the arrow icon blue when open. */
      '& > svg': { color: open ? theme.appColors.primary : undefined },
      /** Double selector (&&) for specificity over MUI's internal emotion styles. */
      '&&': {
        ...borderProps,
        paddingBottom: theme.spacing(0.75),
        paddingLeft: selectStyles?.paddingLeft ?? theme.spacing(1),
        paddingRight: theme.spacing(3.5),
        paddingTop: theme.spacing(0.75),
      },
      '&:focus': {
        backgroundColor: 'inherit',
        borderRadius: theme.spacing(0.375),
      },
      '&:hover': {
        backgroundColor: disabled ? 'transparent' : theme.palette.action.hover,
      },
      alignItems: 'center',
      borderRadius: 3,
      boxSizing: 'border-box',
      display: 'flex',
      height: '100%',
      justifyContent: 'start',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '100%',
    },

    text: {
      '&&': {
        color: disabled ? theme.palette.action.disabled : theme.appColors.iconsType,
        fontSize: 14,
      },
      fontFamily: theme.typography.fontFamily,
      marginRight: theme.spacing(1),
    },
  };
});

interface SelectStyles {
  paddingLeft: number;
}

export type ToolbarSelectProps = SelectProps & {
  open: boolean;
  arrowIcon?: SelectProps['IconComponent'];
  border?: boolean;
  children?: React.ReactNode;
  classes?: SelectProps['classes'];
  className?: SelectProps['className'];
  customRenderValue?: string;
  disableBorderColor?: boolean;
  fullWidth?: SelectProps['fullWidth'];
  IconComponent?: SelectProps['IconComponent'];
  input?: SelectProps['input'];
  menuAttribute?: string;
  MenuProps?: Partial<MenuProps>;
  onClose?: SelectProps['onClose'];
  onOpen?: SelectProps['onOpen'];
  onSelectChange?: SelectProps['onChange'];
  renderValue?: SelectProps['renderValue'];
  selectAttribute?: string;
  selectStyles?: SelectStyles;
  spanAttribute?: string;
  value?: SelectProps['value'];
};

export function ToolbarSelectArrowIcon(): JSX.Element {
  return <ArrowDownIcon iconStyles={{ height: 12, right: 8, width: 12 }} />;
}

interface DefaultInputProps {
  disableColor: boolean;
  open: boolean;
}

const DefaultInput = forwardRef(function DefaultInput(props: DefaultInputProps, ref): JSX.Element {
  const { open, ...rest } = props;
  return <ToolbarInput ref={ref} open={open} {...rest} />;
});

const ToolbarSelectWithRef = forwardRef(function ToolbarSelect(
  props: ToolbarSelectProps,
  ref
): JSX.Element {
  const {
    MenuProps,
    'aria-label': ariaLabel,
    arrowIcon = ToolbarSelectArrowIcon,
    border = true,
    children,
    classes,
    customRenderValue,
    disableBorderColor = false,
    menuAttribute,
    onClose,
    onOpen,
    onSelectChange,
    open,
    renderValue,
    selectAttribute,
    selectStyles,
    spanAttribute,
    value,
    ...rest
  } = props;
  const { input = <DefaultInput disableColor={disableBorderColor} open={open} {...rest} /> } =
    props;
  const { classes: styles } = useToolbarSelectStyles({
    border,
    disableBorderColor,
    disabled: props.disabled,
    open,
    selectStyles,
  });
  const defaultMenuProps = {
    anchorOrigin: { horizontal: 'left' as const, vertical: 'bottom' as const },
    'data-cy': menuAttribute,
    transformOrigin: { horizontal: 'left' as const, vertical: 'top' as const },
    variant: 'menu' as const,
  } as Partial<MenuProps>;

  const text =
    typeof customRenderValue === 'string'
      ? customRenderValue
      : typeof value === 'string'
        ? value
        : undefined;

  function handleRenderValue(): React.ReactNode {
    return (
      <Typography noWrap className={styles.text} data-cy={spanAttribute}>
        {text}
      </Typography>
    );
  }

  return (
    <Select
      ref={ref}
      IconComponent={arrowIcon}
      MenuProps={{
        ...defaultMenuProps,
        ...MenuProps,
      }}
      SelectDisplayProps={
        {
          'aria-label': ariaLabel,
          'data-cy': selectAttribute,
        } as React.HTMLAttributes<HTMLDivElement>
      }
      classes={{
        ...classes,
        select: clsx(styles.select, classes?.select),
      }}
      displayEmpty={true}
      input={input}
      open={open}
      renderValue={renderValue || handleRenderValue}
      value={value}
      onChange={onSelectChange}
      onClose={onClose}
      onOpen={onOpen}
      {...rest}
    >
      {children}
    </Select>
  );
});

export const ToolbarSelect = React.memo(ToolbarSelectWithRef);
